import { openDB } from 'idb';

const DB_NAME = 'pest-patrol-offline';
const QUEUE_STORE = 'api-queue';
const CACHE_STORE = 'daily-cache';
const DB_VERSION = 2;

const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Offline Queue & Daily Cache System
 *
 * Technician-only offline support:
 *  - daily-cache: today's visit data, populated when the technician dashboard loads
 *  - api-queue: queued write operations (service completions) for replay when online
 *
 * Both stores are cleared on logout and stale data is purged on startup (24h TTL).
 */

// ─── IndexedDB ───────────────────────────────────────────────────────────────

async function getDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(QUEUE_STORE)) {
        db.createObjectStore(QUEUE_STORE, { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(CACHE_STORE)) {
        db.createObjectStore(CACHE_STORE, { keyPath: 'technician_id' });
      }
    },
  });
}

// ─── Daily Cache ─────────────────────────────────────────────────────────────

/**
 * Cache today's visits for a technician.
 * @param {number} technicianId
 * @param {Array} visits - array of visit objects from the API
 */
export async function cacheDailyVisits(technicianId, visits) {
  try {
    const db = await getDB();
    const today = new Date().toISOString().split('T')[0];
    await db.put(CACHE_STORE, {
      technician_id: technicianId,
      cached_date: today,
      timestamp: Date.now(),
      visits: visits || [],
    });
    console.log(`[OfflineCache] Cached ${(visits || []).length} visits for technician ${technicianId}`);
  } catch (error) {
    console.error('[OfflineCache] Error caching visits:', error);
  }
}

/**
 * Retrieve cached visits for today. Returns [] if no cache or cache is stale.
 */
export async function getCachedVisits() {
  try {
    const db = await getDB();
    const all = await db.getAll(CACHE_STORE);
    if (all.length === 0) return [];

    const today = new Date().toISOString().split('T')[0];
    const entry = all.find((e) => e.cached_date === today);
    if (!entry) return [];

    // Check TTL
    if (Date.now() - entry.timestamp > TTL_MS) {
      await db.delete(CACHE_STORE, entry.technician_id);
      return [];
    }

    return entry.visits || [];
  } catch (error) {
    console.error('[OfflineCache] Error reading cache:', error);
    return [];
  }
}

/**
 * Clear the daily cache store.
 */
export async function clearDailyCache() {
  try {
    const db = await getDB();
    await db.clear(CACHE_STORE);
    console.log('[OfflineCache] Daily cache cleared');
  } catch (error) {
    console.error('[OfflineCache] Error clearing daily cache:', error);
  }
}

// ─── Write Queue ─────────────────────────────────────────────────────────────

/**
 * Queue a request for later replay.
 */
export async function queueRequest(method, url, data = null) {
  try {
    const db = await getDB();
    const request = {
      method: method.toUpperCase(),
      url,
      data,
      timestamp: Date.now(),
    };
    await db.add(QUEUE_STORE, request);
    console.log(`[OfflineQueue] Request queued: ${method} ${url}`);
    return { queued: true, timestamp: request.timestamp };
  } catch (error) {
    console.error('[OfflineQueue] Error queuing request:', error);
    throw error;
  }
}

/**
 * Get all queued requests.
 */
export async function getQueuedRequests() {
  try {
    const db = await getDB();
    return await db.getAll(QUEUE_STORE);
  } catch (error) {
    console.error('[OfflineQueue] Error getting queued requests:', error);
    return [];
  }
}

/**
 * Clear the entire queue.
 */
export async function clearQueue() {
  try {
    const db = await getDB();
    await db.clear(QUEUE_STORE);
    console.log('[OfflineQueue] Queue cleared');
  } catch (error) {
    console.error('[OfflineQueue] Error clearing queue:', error);
  }
}

/**
 * Remove a specific request from queue.
 */
export async function removeQueuedRequest(id) {
  try {
    const db = await getDB();
    await db.delete(QUEUE_STORE, id);
    console.log(`[OfflineQueue] Request ${id} removed from queue`);
  } catch (error) {
    console.error('[OfflineQueue] Error removing request:', error);
  }
}

/**
 * Process queued requests sequentially.
 * If a 403 with turnstile_challenge is encountered, pause and return with
 * challengePaused: true so the caller can trigger the challenge overlay.
 */
export async function processQueue(apiClient) {
  try {
    const requests = await getQueuedRequests();

    if (requests.length === 0) {
      console.log('[OfflineQueue] No requests to process');
      return { processed: 0, failed: 0, results: [], challengePaused: false };
    }

    console.log(`[OfflineQueue] Processing ${requests.length} queued request(s)...`);

    const results = [];
    let processed = 0;
    let failed = 0;

    for (const req of requests) {
      try {
        let response;

        switch (req.method) {
          case 'GET':
            response = await apiClient.get(req.url);
            break;
          case 'POST':
            response = await apiClient.post(req.url, req.data);
            break;
          case 'PUT':
            response = await apiClient.put(req.url, req.data);
            break;
          case 'PATCH':
            response = await apiClient.patch(req.url, req.data);
            break;
          case 'DELETE':
            response = await apiClient.delete(req.url);
            break;
          default:
            throw new Error(`Unsupported method: ${req.method}`);
        }

        await removeQueuedRequest(req.id);
        processed++;

        results.push({
          id: req.id,
          method: req.method,
          url: req.url,
          success: true,
          response: response.data,
        });

        console.log(`[OfflineQueue] Successfully processed: ${req.method} ${req.url}`);
      } catch (error) {
        // Check if this is a turnstile challenge
        if (
          error?.response?.status === 403 &&
          error?.response?.data?.turnstile_challenge
        ) {
          console.log('[OfflineQueue] Turnstile challenge received -- pausing queue replay');
          return { processed, failed, results, challengePaused: true };
        }

        failed++;

        results.push({
          id: req.id,
          method: req.method,
          url: req.url,
          success: false,
          error: error.message,
        });

        console.error(`[OfflineQueue] Failed to process: ${req.method} ${req.url}`, error);
      }
    }

    console.log(`[OfflineQueue] Processing complete: ${processed} successful, ${failed} failed`);
    return { processed, failed, results, challengePaused: false };
  } catch (error) {
    console.error('[OfflineQueue] Error processing queue:', error);
    return { processed: 0, failed: 0, results: [], error: error.message, challengePaused: false };
  }
}

/**
 * Get queue statistics.
 */
export async function getQueueStats() {
  try {
    const requests = await getQueuedRequests();
    const stats = {
      total: requests.length,
      methods: {},
      oldest: null,
      newest: null,
    };

    if (requests.length > 0) {
      requests.forEach((req) => {
        stats.methods[req.method] = (stats.methods[req.method] || 0) + 1;
      });
      stats.oldest = requests.reduce((oldest, req) =>
        req.timestamp < oldest.timestamp ? req : oldest, requests[0]);
      stats.newest = requests.reduce((newest, req) =>
        req.timestamp > newest.timestamp ? req : newest, requests[0]);
    }

    return stats;
  } catch (error) {
    console.error('[OfflineQueue] Error getting queue stats:', error);
    return { total: 0, methods: {}, oldest: null, newest: null };
  }
}

// ─── Cleanup & Lifecycle ─────────────────────────────────────────────────────

/**
 * Clear all offline data (both cache and queue). Called on logout.
 */
export async function clearAllOfflineData() {
  try {
    const db = await getDB();
    await db.clear(QUEUE_STORE);
    await db.clear(CACHE_STORE);
    console.log('[OfflineQueue] All offline data cleared');
  } catch (error) {
    console.error('[OfflineQueue] Error clearing all offline data:', error);
  }
}

/**
 * Purge stale data older than 24 hours. Called on app startup.
 */
export async function purgeStaleData() {
  try {
    const db = await getDB();
    const cutoff = Date.now() - TTL_MS;

    // Purge stale daily cache entries
    const cacheEntries = await db.getAll(CACHE_STORE);
    for (const entry of cacheEntries) {
      if (entry.timestamp && entry.timestamp < cutoff) {
        await db.delete(CACHE_STORE, entry.technician_id);
        console.log(`[OfflineQueue] Purged stale cache for technician ${entry.technician_id}`);
      }
    }

    // Purge stale queue entries
    const queueEntries = await db.getAll(QUEUE_STORE);
    for (const entry of queueEntries) {
      if (entry.timestamp && entry.timestamp < cutoff) {
        await db.delete(QUEUE_STORE, entry.id);
        console.log(`[OfflineQueue] Purged stale queue entry ${entry.id}`);
      }
    }
  } catch (error) {
    console.error('[OfflineQueue] Error purging stale data:', error);
  }
}

// ─── Utilities ───────────────────────────────────────────────────────────────

/**
 * Check if the browser is currently online.
 */
export function isOnline() {
  return navigator.onLine;
}

/**
 * Setup online/offline event listeners.
 */
export function setupOfflineListener(onOnline, onOffline) {
  const handleOnline = () => {
    console.log('[OfflineQueue] Connection restored');
    if (onOnline) onOnline();
  };

  const handleOffline = () => {
    console.log('[OfflineQueue] Connection lost');
    if (onOffline) onOffline();
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}

const offlineQueueService = {
  queueRequest,
  getQueuedRequests,
  clearQueue,
  removeQueuedRequest,
  processQueue,
  getQueueStats,
  isOnline,
  setupOfflineListener,
  cacheDailyVisits,
  getCachedVisits,
  clearDailyCache,
  clearAllOfflineData,
  purgeStaleData,
};

export default offlineQueueService;
